/**
 * The per-key recommendation summaries the report card shows for each finding.
 *
 * THE FULL SHEET IS THE SOURCE OF TRUTH, NOT THIS FILE. docs/compliance/REMEDIATION_REFERENCE.md is
 * the one place fixes live — every key here has a matching heading there, with its ranked fixes,
 * the reason it fails, and the standard citations. This file deliberately carries only a single
 * concise top recommendation per key, so the card can tell an author what to do first without
 * re-stating the whole reference (which would then drift from it). The card also shows the key
 * itself, so a reader can look up the authoritative detail.
 *
 * Each `doer` tag mirrors the reference's own tags: who can carry the fix out.
 */

export type RemediationDoer = 'PFB' | 'TOOL' | 'SERVICE' | 'HUMAN';

export interface RemediationRecommendation {
  /** The single top recommendation, best-first per the reference's ranking. One sentence. */
  summary: string;
  /** Who can do the top fix — the reference's [PFB]/[TOOL]/[SERVICE]/[HUMAN] tag. */
  doer: RemediationDoer;
}

/**
 * Keyed by the stable remediation key the finding carries. Keys are the same strings the backend
 * RemediationKey emits and the same headings REMEDIATION_REFERENCE.md uses. Only the keys the
 * scanner actually emits are listed; an unknown key falls back to a generic pointer (see
 * recommendationFor) rather than inventing advice.
 */
const RECOMMENDATIONS: Record<string, RemediationRecommendation> = {
  // DOCUMENT TIER
  'DOC-NOT-MARKED': {
    summary: 'Mark the catalog as tagged (/MarkInfo /Marked true) — but only once the document really is tagged.',
    doer: 'PFB',
  },
  'DOC-NO-LANG': {
    summary: 'Set the document language (e.g. en-US) so a screen reader pronounces it correctly.',
    doer: 'PFB',
  },
  'DOC-NO-TITLE': {
    summary: 'Give the file a human title and turn on DisplayDocTitle so readers announce it instead of the filename.',
    doer: 'PFB',
  },
  '5-1': {
    summary: 'Leave the PDF/UA claim off until the file genuinely conforms; it is a statement of fact, not a toggle.',
    doer: 'HUMAN',
  },

  // CONTENT TIER
  '7.1-3': {
    summary: 'Tag the on-page content (or mark it an artifact) so assistive technology can reach it in reading order.',
    doer: 'PFB',
  },
  'ZERO-HEADINGS': {
    summary: 'Author heading regions over the visible headings; the level (H1 vs H2) is a human call.',
    doer: 'HUMAN',
  },
  'ZERO-P': {
    summary: 'Wrap the body prose in real paragraph regions so it is in the tree and prompts attach to their fields.',
    doer: 'PFB',
  },
  'FIGURE-NO-ALT': {
    summary: 'Decide what each image is — information, text, or decoration — then give it /Alt, /ActualText, or artifact.',
    doer: 'HUMAN',
  },

  // INTERACTIVE TIER
  'FIELD-NO-TU': {
    summary: 'Give each field a label, written as the question it asks; the label becomes the spoken name (/TU).',
    doer: 'PFB',
  },
  '7.18.5-2': {
    summary: "Add a /Contents description stating each link's purpose, not the raw URL.",
    doer: 'PFB',
  },
  '7.18.3-1': {
    summary: 'Set each annotated page’s /Tabs to /S so keyboard focus follows the logical order.',
    doer: 'PFB',
  },
};

/** The URL fragment of the reference section for a key, so the UI can point precisely at it. */
export const REMEDIATION_REFERENCE_PATH = 'docs/compliance/REMEDIATION_REFERENCE.md';

/**
 * The recommendation for a finding's key. Returns a generic pointer rather than inventing advice
 * when a key is not one the card has a summary for — the authoritative sheet still has it.
 */
export function recommendationFor(key: string): RemediationRecommendation {
  return (
    RECOMMENDATIONS[key] ?? {
      summary: `See ${REMEDIATION_REFERENCE_PATH} (key ${key}) for the recommended fixes.`,
      doer: 'HUMAN',
    }
  );
}
